// La caché de estaciones del widget.
//
// El problema que resuelve: `Search/stationsRenfe` va a base de datos en cada consulta —la
// clase lleva `$cache = false`— y tarda entre 200 y 500 ms medidos contra producción. Con el
// antirrebote de 250 ms del autocompletado, el cliente ve la lista medio segundo después de
// dejar de teclear, y eso en cada campo y en cada visita.
//
// Cómo se resuelve, en dos capas:
//
//   1. CACHÉ POR CONSULTA. Lo que el servidor contesta se guarda —en memoria y en
//      localStorage— con la consulta como clave. La segunda vez que alguien escribe "mad",
//      la lista sale sin pedir nada.
//   2. EL CATÁLOGO ENTERO, de una sola vez, en segundo plano y sin que nadie espere:
//      `Search/stationsRenfeAll` devuelve las ~500 estaciones con `isFav`, cacheado en el
//      servidor y con cabeceras de caché. Con eso el autocompletado no vuelve a pedir nada.
//
// Por qué hizo falta ese endpoint: el de autocompletar filtra por nombre y devuelve como mucho
// 20 filas (`LIMIT 20` en SearchStationsRenfe), así que el catálogo NO se puede reconstruir a
// base de consultas — medido, con las 26 letras salen 210 estaciones (13 de esas consultas
// vienen cortadas por el tope) y con 100 consultas de dos letras aparecen 274 más.
//
// Mientras el catálogo no esté completo, lo que hay en la caché es un ADELANTO y no la verdad:
// se pinta al instante y se pide igualmente, y cuando llega la respuesta manda ella. Así el
// cliente gana tiempo sin perder estaciones. Cuando el catálogo sí está entero, el filtrado
// local es tan bueno como el del servidor.

const VERSION = 'v1'
const TTL_MS = 7 * 24 * 60 * 60 * 1000      // una semana
const MAX_CONSULTAS = 300                   // tope de entradas guardadas, por no crecer sin fin
const LETRAS = 'abcdefghijklmnopqrstuvwxyz'.split('')
const A_LA_VEZ = 4                          // peticiones simultáneas de la precarga

/** Quita acentos y pasa a minúsculas, como hace la comparación del servidor. */
export function normalizar (texto) {
  return String(texto == null ? '' : texto)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().trim()
}

/**
 * Lo mismo pero SIN recortar los espacios, que es como MySQL ve el nombre de la estación.
 * Importa: varios nombres acaban en espacio ("VALLECAS ", "SALOMÓ ") y ese carácter cuenta en
 * la distancia. Recortándolo, el orden dejaba de parecerse al del servidor.
 */
function plano (texto) {
  return String(texto == null ? '' : texto)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/**
 * La distancia de Levenshtein, que es por lo que ordena el servidor
 * (`ORDER BY isFav DESC, LEVENSHTEIN(e.nombre, :query) ASC, e.nombre ASC`).
 *
 * CON UNA TRAMPA QUE HAY QUE SABER: ese `:query` es EL MISMO parámetro del `LIKE`, así que a
 * LEVENSHTEIN no le llega "ca" sino **"%ca%"**, con los porcentajes dentro. No es un detalle:
 * es lo que hace que para "ca" salga OCAÑA la primera (distancia 3) por delante de CADIZ (4).
 * Comprobado en la base de datos y contra el endpoint: replicándolo así, el orden local
 * coincide con el del servidor en las 25 consultas probadas.
 */
function distancia (a, b) {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  let fila = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    let anterior = fila[0]
    fila[0] = i
    for (let j = 1; j <= b.length; j++) {
      const guardado = fila[j]
      fila[j] = a[i - 1] === b[j - 1]
        ? anterior
        : 1 + Math.min(anterior, fila[j], fila[j - 1])
      anterior = guardado
    }
  }
  return fila[b.length]
}

/**
 * localStorage, siempre entre try/catch: en una ventana privada, con las cookies de terceros
 * bloqueadas o con el disco lleno, cualquiera de las dos operaciones lanza. Si no se puede
 * guardar, el widget funciona igual — sólo pierde la caché entre visitas.
 */
function leerAlmacen (clave) {
  try {
    const crudo = window.localStorage.getItem(clave)
    if (!crudo) return null
    const datos = JSON.parse(crudo)
    if (!datos || datos.version !== VERSION) return null
    if (Date.now() - datos.cuando > TTL_MS) return null
    return datos
  } catch (e) { return null }
}

function escribirAlmacen (clave, datos) {
  try {
    window.localStorage.setItem(clave, JSON.stringify(datos))
  } catch (e) { /* sin caché entre visitas, nada más */ }
}

/**
 * @param {object} opciones
 * @param {import('./datos.js').Datos} opciones.datos
 * @param {string} [opciones.marca]
 * @param {string} [opciones.locale]
 * @param {string} [opciones.tipo]  `trenCoche`: el catálogo del destino del tren + coche, que
 *                                  es OTRO (sólo estaciones con oficina de alquiler). Lleva su
 *                                  propia caché: mezclarlo con el general dejaría elegir un
 *                                  destino sin coches.
 */
export function crearCacheEstaciones (opciones = {}) {
  const { datos } = opciones
  const tipo = opciones.tipo || ''
  // La clave lleva marca e idioma porque de las dos depende lo que devuelve el servidor: las
  // estaciones se filtran por marca y los nombres van traducidos. Y el tipo, por lo mismo.
  const clave = 'rvb.estaciones.' + (opciones.marca || '-') + '.' + (opciones.locale || 'es')
    + (tipo ? '.' + tipo : '')

  // consulta normalizada -> filas, en el orden en que las devolvió el servidor
  const porConsulta = new Map()
  // id -> fila, para resolver un origen/destino predefinido sin preguntar
  const porId = new Map()
  let precargando = null
  // Si el catálogo está entero, el filtrado local es tan bueno como el del servidor y ya no
  // hace falta revalidar cada consulta.
  let completo = false

  const guardado = leerAlmacen(clave)
  if (guardado) {
    if (guardado.consultas) {
      for (const [q, filas] of Object.entries(guardado.consultas)) {
        porConsulta.set(q, filas)
        filas.forEach((e) => porId.set(String(e.id), e))
      }
    }
    if (Array.isArray(guardado.catalogo)) {
      guardado.catalogo.forEach((e) => porId.set(String(e.id), e))
      completo = guardado.catalogo.length > 0
    }
  }

  function volcar () {
    // Se guardan las últimas MAX_CONSULTAS: las entradas van en orden de inserción, así que
    // basta con quedarse con la cola.
    const entradas = [...porConsulta.entries()].slice(-MAX_CONSULTAS)
    escribirAlmacen(clave, {
      version: VERSION,
      cuando: Date.now(),
      consultas: Object.fromEntries(entradas),
      catalogo: completo ? [...porId.values()] : undefined,
    })
  }

  /** Mete en la caché lo que ha contestado el servidor. */
  function recordar (q, filas) {
    if (!Array.isArray(filas)) return
    porConsulta.set(normalizar(q), filas)
    filas.forEach((e) => { if (e && e.id !== undefined) porId.set(String(e.id), e) })
    volcar()
  }

  /**
   * Lo que se puede enseñar YA para una consulta, o null si no hay nada que enseñar.
   *
   * Primero mira si esa consulta exacta está cacheada —entonces las filas son las del
   * servidor, en su orden— y si no, arma un adelanto con las estaciones que conoce de otras
   * consultas, ordenadas como ordena el servidor. Devolver null es correcto y frecuente: el
   * autocompletado se limita entonces a esperar la respuesta, como hasta ahora.
   */
  function adelanto (q) {
    const busca = normalizar(q)
    if (!busca) return null
    if (porConsulta.has(busca)) return porConsulta.get(busca)
    if (!porId.size) return null
    // Lo que recibe LEVENSHTEIN en el servidor, porcentajes incluidos (ver arriba).
    const patron = '%' + busca + '%'
    const filas = [...porId.values()]
      .filter((e) => plano(e.nombre).indexOf(busca) !== -1)
      .sort((a, b) => {
        // `isfav` sólo viene en el catálogo entero; en lo cacheado por consulta no está, y
        // entonces ese término no desempata nada.
        const fav = (b.isfav ? 1 : 0) - (a.isfav ? 1 : 0)
        if (fav !== 0) return fav
        const d = distancia(plano(a.nombre), patron) - distancia(plano(b.nombre), patron)
        if (d !== 0) return d
        // El desempate, como la collation de MySQL: sin acentos. Es lo que pone CÁRTAMA antes
        // que CATOIRA, que comparando por código de carácter salía al revés.
        const pa = plano(a.nombre)
        const pb = plano(b.nombre)
        return pa < pb ? -1 : (pa > pb ? 1 : 0)
      })
      .slice(0, 20)   // el mismo tope que el servidor
    return filas.length ? filas : null
  }

  /**
   * Resuelve un origen o un destino predefinido. Acepta lo que traiga la landing: el código de
   * estación (MADRI), el id, o el nombre entero o a medias.
   * @returns {Promise<object|null>} la fila entera, que es lo que necesita la URL de salida
   */
  async function resolver (valor) {
    const busca = normalizar(valor)
    if (!busca) return null

    const acierta = (e) => normalizar(e.codigoestacionrenfe) === busca
      || String(e.id) === String(valor).trim()
      || normalizar(e.nombre) === busca

    // 1) lo que ya se conozca, sin pedir nada
    for (const e of porId.values()) if (acierta(e)) return e

    // 2) al servidor, que es quien manda. Se pregunta por el valor tal cual: el filtro es
    //    `LIKE %q%` sobre el nombre, así que un código de estación sólo acierta si además
    //    aparece en el nombre (MADRI/MADRID sí; SAJUS/SEVILLA-SANTA JUSTA no).
    let filas = []
    try { filas = await datos.estaciones(String(valor).trim(), undefined, tipo) } catch (e) { filas = [] }
    if (Array.isArray(filas) && filas.length) {
      recordar(String(valor).trim(), filas)
      const exacta = filas.find(acierta)
      if (exacta) return exacta
      // Sin coincidencia exacta, la primera es la que el servidor considera más parecida:
      // es la misma que elegiría un cliente que escribe eso y pulsa la primera fila.
      return filas[0]
    }

    // 3) por código: si el código no está en el nombre, la única forma de dar con él es
    //    haberlo precargado. Se espera a la precarga, que ya estará en marcha.
    if (precargando) {
      try { await precargando } catch (e) { /* da igual: abajo se mira lo que haya */ }
      for (const e of porId.values()) if (acierta(e)) return e
    }
    return null
  }

  /**
   * Llena la caché en segundo plano. NADIE espera a esto: se lanza al montar y el widget sigue
   * funcionando igual mientras tanto.
   *
   * Hoy son 26 peticiones (una por letra), de cuatro en cuatro, que en producción tardan unos
   * 2 s en total y traen 210 estaciones. No son todas —el `LIMIT 20` del endpoint corta 13 de
   * esas 26 consultas— y por eso la caché es un adelanto y no la verdad. Si algún día hay un
   * endpoint que devuelva el catálogo entero, esto es lo único que hay que cambiar.
   */
  function cargarCatalogo () {
    if (precargando) return precargando
    if (completo) { precargando = Promise.resolve(); return precargando }

    precargando = (async () => {
      // UNA petición: el servidor devuelve el catálogo entero, cacheado en disco y con
      // cabeceras de caché. Son unas 500 estaciones y 64 KB (bastantes menos comprimidas).
      try {
        const filas = await datos.estacionesTodas(undefined, tipo)
        if (Array.isArray(filas) && filas.length) {
          filas.forEach((e) => { if (e && e.id !== undefined) porId.set(String(e.id), e) })
          completo = true
          volcar()
          return
        }
      } catch (e) { /* abajo */ }

      // Si el servidor todavía no tiene ese endpoint, NO se cae a pedir letra por letra: eso
      // eran 26 peticiones para un catálogo que igualmente salía incompleto. Se sigue con la
      // caché por consulta, que no cuesta nada, y ya está.
      console.warn('[buscador] no he podido traerme el catálogo de estaciones; '
        + 'el autocompletado sigue funcionando contra el servidor')
    })()
    return precargando
  }

  /** La precarga a base de consultas de una letra, que ya casi nunca hace falta. */
  function cargarPorLetras () {
    return (async () => {
      const pendientes = LETRAS.filter((l) => !porConsulta.has(l))
      for (let i = 0; i < pendientes.length; i += A_LA_VEZ) {
        await Promise.all(pendientes.slice(i, i + A_LA_VEZ).map(async (letra) => {
          try {
            const filas = await datos.estaciones(letra, undefined, tipo)
            if (Array.isArray(filas)) {
              porConsulta.set(letra, filas)
              filas.forEach((e) => { if (e && e.id !== undefined) porId.set(String(e.id), e) })
            }
          } catch (e) { /* una letra que falle no rompe la precarga */ }
        }))
      }
      volcar()
    })()
  }

  /** Arranca la precarga cuando el navegador esté ocioso, para no competir con la página. */
  function precargarCuandoHaya (retraso = 1200) {
    const lanzar = () => { cargarCatalogo() }
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(lanzar, { timeout: 5000 })
    } else {
      setTimeout(lanzar, retraso)
    }
  }

  return {
    tipo,
    /** La consulta al servidor de ESTE catálogo, para el autocompletado. */
    buscar: (q, señal) => datos.estaciones(q, señal, tipo),
    adelanto,
    recordar,
    resolver,
    cargarCatalogo,
    cargarPorLetras,
    precargarCuandoHaya,
    /** ¿Está el catálogo entero? Entonces el adelanto es tan bueno como la respuesta. */
    estaCompleto: () => completo,
    /** Para las pruebas y para mirar por consola qué tiene dentro. */
    cuantas: () => porId.size,
  }
}
